import { QueryObserver, queryOptions } from '@tanstack/react-query'

import { UserApi } from 'shared/api/UserApi'
import queryClient from 'shared/lib/query-client'

interface OrdersCountData {
  count: number
  growthRevision: number
}

let claimedGrowthRevision = 0

export const ordersCountQueryOptions = queryOptions({
  queryKey: ['profile', 'orders', 'count'] as const,
  queryFn: async ({ signal }): Promise<OrdersCountData> => {
    const { data } = await UserApi.getMyOrdersCount(signal)
    const totalCount = data.data.total_count

    return {
      count: Number.isFinite(totalCount)
        ? Math.max(0, Math.trunc(totalCount))
        : 0,
      growthRevision: 0,
    }
  },
  structuralSharing: (oldValue, newValue): OrdersCountData => {
    const previous = oldValue as OrdersCountData | undefined
    const next = newValue as OrdersCountData

    if (previous?.count === next.count) return previous

    return {
      count: next.count,
      growthRevision:
        (previous?.growthRevision ?? 0) +
        Number(next.count > (previous?.count ?? 0)),
    }
  },
  staleTime: Infinity,
  gcTime: Infinity,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
})

export const refreshOrdersCount = async (): Promise<void> => {
  const queryKey = ordersCountQueryOptions.queryKey
  const unsubscribe = new QueryObserver(queryClient, {
    ...ordersCountQueryOptions,
    enabled: false,
  }).subscribe(() => {})

  try {
    await queryClient.cancelQueries({ queryKey, exact: true })
    await queryClient.invalidateQueries({
      queryKey,
      exact: true,
      refetchType: 'none',
    })
    await queryClient.fetchQuery(ordersCountQueryOptions)
  } catch {
    // Keep the last successfully loaded count.
  } finally {
    unsubscribe()
  }
}

export const resetOrdersCount = (): void => {
  const queryKey = ordersCountQueryOptions.queryKey

  void queryClient.cancelQueries({ queryKey, exact: true })
  queryClient.removeQueries({ queryKey, exact: true })
  claimedGrowthRevision = 0
}

export const hasPendingOrdersBadgeAnimation = (growthRevision: number) =>
  growthRevision > claimedGrowthRevision

export const claimOrdersBadgeAnimation = (growthRevision: number) => {
  if (!hasPendingOrdersBadgeAnimation(growthRevision)) return false

  claimedGrowthRevision = growthRevision
  return true
}
